import React from 'react';
import {
    Box,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Typography,
    Chip,
    Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatCentsToUsd } from '../../../helpers/formatCentsToUsd';
import { formatDateTimeInVenueTz } from '../../../helpers/formatDateTime';
import { getBookingStatusColor } from '../helpers/getBookingStatusColor';
import { useSort } from '../../../hooks/useSort';
import SortableHeaderCell from '../../../components/atom/SortableHeaderCell';

const renderBalance = (item) => {
    if (item.unrealisedBalanceRef) {
        return (
            <Typography variant="caption" color="text.secondary">
                ↑
            </Typography>
        );
    }
    if (!item.unrealisedBalance) return '-';
    const color = item.unrealisedBalance > 0 ? 'success.main' : 'error.main';
    return (
        <Typography variant="body2" color={color}>
            {formatCentsToUsd(item.unrealisedBalance)}
        </Typography>
    );
};

const SalesReportTable = ({ table, taxBreakout, showIntegrationPrice, showUnrealisedBalance }) => {
    const { t } = useTranslation();
    const sort = useSort(table.items, 'bookingId', 'asc');

    return (
        <Box sx={{ mb: 3, width: '100%' }}>
            {/* Table Title */}
            <Typography variant="h6" sx={{ mb: 1 }}>
                {table.showTitle} - {table.ticketTypeName}
            </Typography>

            <TableContainer sx={{ maxHeight: '65vh' }}>
                <Table stickyHeader sx={{ width: '100%', '& thead th': { top: 0 } }}>
                    <TableHead>
                        <TableRow>
                            <SortableHeaderCell field="bookingId" sort={sort}>
                                {t('reports.columns.bookingId')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="bookingStatus" sort={sort}>
                                {t('reports.columns.bookingStatus')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="contact" sort={sort}>
                                {t('reports.columns.contact')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="voucher" sort={sort}>
                                {t('reports.columns.voucher')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="customers" sort={sort}>
                                {t('reports.columns.customers')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="availability" sort={sort}>
                                {t('reports.columns.showDate')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="createdAt" sort={sort}>
                                {t('reports.columns.createdAt')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="sourceType" sort={sort}>
                                {t('reports.columns.source')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="commissionGroupName" sort={sort}>
                                {t('reports.columns.commissionGroup')}
                            </SortableHeaderCell>
                            {taxBreakout && (
                                <>
                                    <SortableHeaderCell field="netAmount" sort={sort} align="right">
                                        {t('reports.columns.netAmount')}
                                    </SortableHeaderCell>
                                    <SortableHeaderCell field="taxAmount" sort={sort} align="right">
                                        {t('reports.columns.taxAmount')}
                                    </SortableHeaderCell>
                                </>
                            )}
                            <SortableHeaderCell field="ticketAmount" sort={sort} align="right">
                                {t('reports.columns.ticketAmount', 'Ticket amount')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="bookingFees" sort={sort} align="right">
                                {t('reports.columns.bookingFees', 'Booking fees')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="amountDue" sort={sort} align="right">
                                {t('reports.columns.amountDue')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="processingFee" sort={sort} align="right">
                                {t('reports.columns.processingFee')}
                            </SortableHeaderCell>
                            <SortableHeaderCell
                                field="absorbedProcessingFee"
                                sort={sort}
                                align="right"
                            >
                                {t('reports.columns.absorbedProcessingFee')}
                            </SortableHeaderCell>
                            {showIntegrationPrice && (
                                <SortableHeaderCell
                                    field="integrationPrice"
                                    sort={sort}
                                    align="right"
                                >
                                    {t('reports.columns.integrationPrice')}
                                </SortableHeaderCell>
                            )}
                            {showUnrealisedBalance && (
                                <SortableHeaderCell
                                    field="unrealisedBalance"
                                    sort={sort}
                                    align="right"
                                >
                                    <Tooltip
                                        title={t('reports.columns.unrealisedBalanceTooltip')}
                                        arrow
                                    >
                                        <span style={{ cursor: 'help' }}>
                                            {t('reports.columns.unrealisedBalance')}
                                        </span>
                                    </Tooltip>
                                </SortableHeaderCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sort.sortedItems.map((item) => (
                            <TableRow key={item.bookingId}>
                                <TableCell>{item.bookingId}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={t(`bookingstatus.${item.bookingStatus}`)}
                                        color={getBookingStatusColor(item.bookingStatus)}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    {item.contact}
                                    <br />
                                    <Typography variant="caption" color="text.secondary">
                                        {item.email}
                                    </Typography>
                                </TableCell>
                                <TableCell>{item.voucher}</TableCell>
                                <TableCell>{item.customers}</TableCell>
                                <TableCell>{item.availability}</TableCell>
                                <TableCell>{formatDateTimeInVenueTz(item.createdAt)}</TableCell>
                                <TableCell>
                                    {item.sourceType === 'affiliate' ? 'Affiliate' : 'Online'}
                                </TableCell>
                                <TableCell>{item.commissionGroupName || '-'}</TableCell>
                                {taxBreakout && (
                                    <>
                                        <TableCell align="right">
                                            {formatCentsToUsd(item.netAmount || 0)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatCentsToUsd(item.taxAmount || 0)}
                                        </TableCell>
                                    </>
                                )}
                                <TableCell align="right">
                                    {formatCentsToUsd(item.ticketAmount || 0)}
                                </TableCell>
                                <TableCell align="right">
                                    {formatCentsToUsd(item.bookingFees || 0)}
                                </TableCell>
                                <TableCell align="right">
                                    {formatCentsToUsd(item.amountDue)}
                                </TableCell>
                                <TableCell align="right">
                                    {formatCentsToUsd(item.processingFee || 0)}
                                </TableCell>
                                <TableCell align="right">
                                    {formatCentsToUsd(item.absorbedProcessingFee || 0)}
                                </TableCell>
                                {showIntegrationPrice && (
                                    <TableCell align="right">
                                        {item.integrationPrice
                                            ? formatCentsToUsd(item.integrationPrice)
                                            : '-'}
                                    </TableCell>
                                )}
                                {showUnrealisedBalance && (
                                    <TableCell align="right">{renderBalance(item)}</TableCell>
                                )}
                            </TableRow>
                        ))}

                        {/* Summary Row */}
                        <TableRow sx={{ backgroundColor: 'action.hover' }}>
                            {/* bookingId */}
                            <TableCell>
                                <strong>{t('reports.summary.total')}</strong>
                            </TableCell>
                            {/* bookingStatus */}
                            <TableCell />
                            {/* contact */}
                            <TableCell>
                                <strong>
                                    {t('reports.summary.bookings', {
                                        count: table.summary.bookingCount,
                                    })}
                                </strong>
                            </TableCell>
                            {/* voucher */}
                            <TableCell />
                            {/* customers */}
                            <TableCell>
                                <strong>
                                    {t('reports.summary.customers', {
                                        count: table.summary.customerCount,
                                    })}
                                </strong>
                            </TableCell>
                            {/* showDate */}
                            <TableCell />
                            {/* createdAt */}
                            <TableCell />
                            {/* source */}
                            <TableCell />
                            {/* commissionGroup */}
                            <TableCell />
                            {taxBreakout && (
                                <>
                                    <TableCell align="right">
                                        <strong>
                                            {formatCentsToUsd(table.summary.totalNetAmount || 0)}
                                        </strong>
                                    </TableCell>
                                    <TableCell align="right">
                                        <strong>
                                            {formatCentsToUsd(table.summary.totalTaxAmount || 0)}
                                        </strong>
                                    </TableCell>
                                </>
                            )}
                            <TableCell align="right">
                                <strong>
                                    {formatCentsToUsd(table.summary.totalTicketAmount || 0)}
                                </strong>
                            </TableCell>
                            <TableCell align="right">
                                <strong>
                                    {formatCentsToUsd(table.summary.totalBookingFees || 0)}
                                </strong>
                            </TableCell>
                            <TableCell align="right">
                                <strong>{formatCentsToUsd(table.summary.totalAmount)}</strong>
                            </TableCell>
                            <TableCell align="right">
                                <strong>
                                    {formatCentsToUsd(table.summary.totalProcessingFees || 0)}
                                </strong>
                            </TableCell>
                            <TableCell align="right">
                                <strong>
                                    {formatCentsToUsd(
                                        table.summary.totalAbsorbedProcessingFees || 0,
                                    )}
                                </strong>
                            </TableCell>
                            {showIntegrationPrice && (
                                <TableCell align="right">
                                    <strong>
                                        {table.summary.totalIntegrationPrice
                                            ? formatCentsToUsd(table.summary.totalIntegrationPrice)
                                            : '-'}
                                    </strong>
                                </TableCell>
                            )}
                            {showUnrealisedBalance && (
                                <TableCell align="right">
                                    <strong>
                                        {table.summary.totalUnrealisedBalance
                                            ? formatCentsToUsd(table.summary.totalUnrealisedBalance)
                                            : '-'}
                                    </strong>
                                </TableCell>
                            )}
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default SalesReportTable;
